import type { SpaceOut } from "../../api/types.gen.ts";
import { type Key, t } from "../../i18n/index.ts";
import { spaceUrl } from "../../lib/paths.ts";
import { atLeast } from "../../lib/roles.ts";
import { PageHeader } from "../../ui/PageHeader.tsx";
import { TabNav } from "../../ui/TabNav.tsx";

/** The space's own tabs, then the organization's (shared by all its spaces), each group labelled. */
const TABS: [string, Key, "space" | "org"][] = [
  ["", "settings.tab.general", "space"],
  ["/hooks", "settings.tab.hooks", "space"],
  ["/org", "settings.tab.general", "org"],
  ["/spaces", "settings.tab.spaces", "org"],
  ["/members", "settings.tab.members", "org"],
  ["/billing", "settings.tab.billing", "org"],
  ["/usage", "settings.tab.usage", "org"],
  ["/keys", "settings.tab.keys", "org"],
  ["/privacy", "settings.tab.privacy", "org"],
];

/** Tabs only admins see. */
const ADMIN_TABS = new Set(["/hooks", "/privacy"]);

/** Title and tabs shared by the settings pages. */
export function SettingsNav({ space, tab }: { space: SpaceOut; tab: string }) {
  const tabs = TABS.filter(([path]) => !ADMIN_TABS.has(path) || atLeast(space.role, "admin"));
  const groups = { space: t("settings.group.space"), org: t("settings.group.org") };
  // The id of what the tab is about: the space's, or the organization's.
  const org = TABS.find(([path]) => path === tab)?.[2] === "org";
  return (
    <>
      <PageHeader title={t(org ? "settings.titleOrg" : "settings.titleSpace")} id={org ? space.org : space.id} />
      <TabNav
        label={t("settings.sections")}
        tabs={tabs.map(([path, label, group]) => ({
          href: spaceUrl(space, `/settings${path}`),
          label: t(label),
          current: path === tab,
          group: groups[group],
        }))}
      />
    </>
  );
}
